import React, { useEffect, useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  InputAdornment,
  IconButton,
  Chip,
  Box,
  Paper,
  Grid,
  Typography,
  Divider,
  Fade,
  Slide,
  Zoom,
  styled,
  css,
} from "@mui/material";
import {
  Person,
  Email,
  LocationOn,
  Phone,
  AssignmentInd,
  Visibility,
  VisibilityOff,
  DirectionsCar,
  Close,
} from "@mui/icons-material";

interface Role {
  id: number;
  name: string;
}

interface Vehicle {
  id: number;
  plateNo: string;
}

interface AddUserDialogProps {
  open: boolean;
  onClose: () => void;
  onUserAdded: () => void;
}

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    background: "linear-gradient(135deg, #1e1e1e 0%, #2a2a2a 100%)",
    border: "2px solid #4caf50",
    borderRadius: "16px",
    boxShadow: "0 10px 30px rgba(0, 0, 0, 0.5)",
    width: "650px",
    maxWidth: "calc(100% - 40px)",
    maxHeight: "90vh",
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
      width: "95%",
    },
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiInputBase-input": {
    color: "#ffffff",
    fontSize: "0.95rem",
    padding: "12px 14px",
  },
  "& .MuiInputLabel-root": {
    color: "rgba(255, 255, 255, 0.7)",
    fontSize: "0.95rem",
  },
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "rgba(255, 255, 255, 0.23)",
      transition: "all 0.3s ease",
    },
    "&:hover fieldset": {
      borderColor: "#4caf50",
      boxShadow: "0 0 0 2px rgba(76, 175, 80, 0.2)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#4caf50",
      boxShadow: "0 0 0 3px rgba(76, 175, 80, 0.3)",
    },
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#4caf50",
  },
}));

const AddUserDialog: React.FC<AddUserDialogProps> = ({
  open,
  onClose,
  onUserAdded,
}) => {
  const [name, setName] = useState("");
  const [password, setPassword] = useState("newuser");
  const [showPassword, setShowPassword] = useState(false);
  const [address, setAddress] = useState("");
  const [email, setEmail] = useState("");
  const [phone_number, setPhoneNumber] = useState("");
  const [fullName, setfullName] = useState("");
  const [is_under, setIsUnder] = useState<number | "">("");
  const [roles, setRoles] = useState<Role[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [selectedVehicles, setSelectedVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [vehicleSearch, setVehicleSearch] = useState("");
  const [showVehicleDropdown, setShowVehicleDropdown] = useState(false);

  const dialogContentRef = useRef<HTMLDivElement>(null);
  const vehicleInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_BASE_URL}/role`)
      .then((res) => res.json())
      .then((data) => setRoles(data))
      .catch((error) => console.error("Error fetching roles:", error));
  }, []);

  useEffect(() => {
    const fetchVehicles = async () => {
      const token: string | null = localStorage.getItem("token");

      if (!token) {
        return;
      }
      try {
        setLoading(true);
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/vehicle/getAllVehicles`,
          {
            headers: {
              "x-api-key": token,
            },
          }
        );
        if (!response.ok) throw new Error("Failed to fetch vehicles");
        const data = await response.json();
        setVehicles(data);
      } finally {
        setLoading(false);
      }
    };

    fetchVehicles();
  }, []);

  const filteredVehicles = vehicles.filter(
    (vehicle) =>
      vehicle.plateNo?.toLowerCase().includes(vehicleSearch.toLowerCase()) &&
      !selectedVehicles.some((v) => v.id === vehicle.id)
  );

  const handleAddVehicle = (vehicle: Vehicle) => {
    setSelectedVehicles([...selectedVehicles, vehicle]);
    setVehicleSearch("");
    setShowVehicleDropdown(false);
  };

  const handleRemoveVehicle = (id: number) => {
    setSelectedVehicles(selectedVehicles.filter((v) => v.id !== id));
  };

  const handleVehicleInputClick = () => {
    setShowVehicleDropdown(true);
    setTimeout(() => {
      if (dialogContentRef.current) {
        dialogContentRef.current.scrollTo({
          top: dialogContentRef.current.scrollHeight,
          behavior: "smooth",
        });
      }
    }, 100);
  };

  const handleSubmit = () => {
    const newUser = {
      name,
      password,
      address,
      email,
      phone_number,
      role: 1,
      is_under,
      fullName,
      vehicleIds: selectedVehicles.map((v) => v.id),
    };
    const token: string | null = localStorage.getItem("token");

    if (!token) {
      return;
    }

    fetch(`${process.env.REACT_APP_API_BASE_URL}/user/createUser/userCreate`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": token,
      },
      body: JSON.stringify(newUser),
    })
      .then((response) => response.json())
      .then(() => {
        onUserAdded();
        onClose();
        setName("");
        setPassword("newuser");
        setAddress("");
        setEmail("");
        setPhoneNumber("");
        setfullName("");
        setIsUnder("");
        setSelectedVehicles([]);
        setVehicleSearch("");
      })
      .catch((error) => console.error("Error adding user:", error));
  };

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      scroll="paper"
      TransitionComponent={Fade}
      transitionDuration={300}
    >
      <DialogTitle
        sx={{
          color: "#4caf50",
          textAlign: "center",
          fontSize: "1.6rem",
          fontWeight: "bold",
          padding: "24px",
          borderBottom: "1px solid rgba(76, 175, 80, 0.3)",
          background:
            "linear-gradient(135deg, rgba(30,30,30,0.9) 0%, rgba(42,42,42,0.9) 100%)",
          position: "sticky",
          top: 0,
          zIndex: 2,
          backdropFilter: "blur(8px)",
        }}
      >
        Add New User
      </DialogTitle>

      <DialogContent
        ref={dialogContentRef}
        sx={{
          padding: "24px",
          background:
            "linear-gradient(135deg, rgba(30,30,30,0.95) 0%, rgba(42,42,42,0.95) 100%)",
          overflowY: "auto",
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#2a2a2a",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#4caf50",
            borderRadius: "4px",
          },
        }}
      >
        {/* Personal Information Section */}
        <Slide in={true} direction="right" timeout={300}>
          <Box>
            <Typography
              variant="h6"
              sx={{
                color: "#4caf50",
                margin: "0 0 16px 0",
                fontWeight: "bold",
                fontSize: "1.2rem",
                display: "flex",
                alignItems: "center",
                "&:before": {
                  content: '""',
                  display: "inline-block",
                  width: "8px",
                  height: "20px",
                  backgroundColor: "#4caf50",
                  marginRight: "12px",
                  borderRadius: "4px",
                },
              }}
            >
              Personal Information
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <StyledTextField
                  fullWidth
                  label="Full Name"
                  value={fullName}
                  onChange={(e) => setfullName(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Person sx={{ color: "rgba(255, 255, 255, 0.7)" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <StyledTextField
                  fullWidth
                  label="Username"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <AssignmentInd
                          sx={{ color: "rgba(255, 255, 255, 0.7)" }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </Slide>

        <Divider
          sx={{
            my: 4,
            borderColor: "rgba(255, 255, 255, 0.12)",
            opacity: 0.5,
          }}
        />

        {/* Contact Information Section */}
        <Slide in={true} direction="right" timeout={400}>
          <Box>
            <Typography
              variant="h6"
              sx={{
                color: "#4caf50",
                margin: "0 0 16px 0",
                fontWeight: "bold",
                fontSize: "1.2rem",
                display: "flex",
                alignItems: "center",
                "&:before": {
                  content: '""',
                  display: "inline-block",
                  width: "8px",
                  height: "20px",
                  backgroundColor: "#4caf50",
                  marginRight: "12px",
                  borderRadius: "4px",
                },
              }}
            >
              Contact Information
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <StyledTextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Email sx={{ color: "rgba(255, 255, 255, 0.7)" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <StyledTextField
                  fullWidth
                  label="Phone Number"
                  type="tel"
                  value={phone_number}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Phone sx={{ color: "rgba(255, 255, 255, 0.7)" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <StyledTextField
                  fullWidth
                  label="Address"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <LocationOn
                          sx={{ color: "rgba(255, 255, 255, 0.7)" }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </Slide>

        <Divider
          sx={{
            my: 4,
            borderColor: "rgba(255, 255, 255, 0.12)",
            opacity: 0.5,
          }}
        />

        {/* Security Section */}
        <Slide in={true} direction="right" timeout={500}>
          <Box>
            <Typography
              variant="h6"
              sx={{
                color: "#4caf50",
                margin: "0 0 16px 0",
                fontWeight: "bold",
                fontSize: "1.2rem",
                display: "flex",
                alignItems: "center",
                "&:before": {
                  content: '""',
                  display: "inline-block",
                  width: "8px",
                  height: "20px",
                  backgroundColor: "#4caf50",
                  marginRight: "12px",
                  borderRadius: "4px",
                },
              }}
            >
              Security
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <StyledTextField
                  fullWidth
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          sx={{ color: "rgba(255, 255, 255, 0.7)" }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <StyledTextField
                  select
                  fullWidth
                  label="Role"
                  value={is_under}
                  onChange={(e) => setIsUnder(Number(e.target.value))}
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        sx: {
                          backgroundColor: "#2d2d2d",
                          color: "#ffffff",
                          "& .MuiMenuItem-root": {
                            "&:hover": {
                              backgroundColor: "rgba(76, 175, 80, 0.1)",
                            },
                          },
                        },
                      },
                    },
                  }}
                >
                  {roles.map((role) => (
                    <MenuItem
                      key={role.id}
                      value={role.id}
                      sx={{ color: "#ffffff" }}
                    >
                      {role.name}
                    </MenuItem>
                  ))}
                </StyledTextField>
              </Grid>
            </Grid>
          </Box>
        </Slide>

        <Divider
          sx={{
            my: 4,
            borderColor: "rgba(255, 255, 255, 0.12)",
            opacity: 0.5,
          }}
        />

        {/* Vehicle Assignment Section */}
        <Slide in={true} direction="right" timeout={600}>
          <Box sx={{ position: "relative" }}>
            <Typography
              variant="h6"
              sx={{
                color: "#4caf50",
                margin: "0 0 16px 0",
                fontWeight: "bold",
                fontSize: "1.2rem",
                display: "flex",
                alignItems: "center",
                "&:before": {
                  content: '""',
                  display: "inline-block",
                  width: "8px",
                  height: "20px",
                  backgroundColor: "#4caf50",
                  marginRight: "12px",
                  borderRadius: "4px",
                },
              }}
            >
              Vehicle Assignment
            </Typography>
            <StyledTextField
              fullWidth
              label="Search vehicles by plate number"
              value={vehicleSearch}
              onChange={(e) => {
                setVehicleSearch(e.target.value);
                setShowVehicleDropdown(true);
              }}
              onClick={handleVehicleInputClick}
              onFocus={handleVehicleInputClick}
              onBlur={() =>
                setTimeout(() => setShowVehicleDropdown(false), 200)
              }
              inputRef={vehicleInputRef}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <DirectionsCar sx={{ color: "rgba(255, 255, 255, 0.7)" }} />
                  </InputAdornment>
                ),
              }}
            />

            {/* Selected vehicles chips */}
            {selectedVehicles.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "8px",
                  maxHeight: "120px",
                  overflowY: "auto",
                  padding: "12px",
                  marginTop: "16px",
                  backgroundColor: "rgba(76, 175, 80, 0.05)",
                  borderRadius: "8px",
                  border: "1px dashed rgba(76, 175, 80, 0.3)",
                  "&::-webkit-scrollbar": {
                    height: "6px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "#4caf50",
                    borderRadius: "3px",
                  },
                }}
              >
                {selectedVehicles.map((vehicle) => (
                  <Zoom in={true} key={vehicle.id}>
                    <Chip
                      label={vehicle.plateNo}
                      onDelete={() => handleRemoveVehicle(vehicle.id)}
                      sx={{
                        color: "#ffffff",
                        backgroundColor: "rgba(76, 175, 80, 0.2)",
                        "& .MuiChip-deleteIcon": {
                          color: "rgba(255, 255, 255, 0.5)",
                          "&:hover": {
                            color: "#ffffff",
                          },
                        },
                      }}
                    />
                  </Zoom>
                ))}
              </Box>
            )}

            {/* Vehicle dropdown */}
            {showVehicleDropdown && filteredVehicles.length > 0 && (
              <Paper
                sx={{
                  position: "absolute",
                  width: "100%",
                  zIndex: 1,
                  mt: "8px",
                  backgroundColor: "#2d2d2d",
                  maxHeight: "200px",
                  overflowY: "auto",
                  boxShadow: "0 10px 20px rgba(0, 0, 0, 0.3)",
                  borderRadius: "8px",
                  border: "1px solid rgba(76, 175, 80, 0.3)",
                  "&::-webkit-scrollbar": {
                    width: "6px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "#4caf50",
                    borderRadius: "3px",
                  },
                }}
              >
                {filteredVehicles.map((vehicle) => (
                  <MenuItem
                    key={vehicle.id}
                    onClick={() => handleAddVehicle(vehicle)}
                    sx={{
                      color: "#ffffff",
                      "&:hover": {
                        backgroundColor: "rgba(76, 175, 80, 0.1)",
                      },
                    }}
                  >
                    {vehicle.plateNo}
                  </MenuItem>
                ))}
              </Paper>
            )}
          </Box>
        </Slide>
      </DialogContent>

      <DialogActions
        sx={{
          padding: "16px 24px",
          borderTop: "1px solid rgba(76, 175, 80, 0.3)",
          justifyContent: "center",
          background:
            "linear-gradient(135deg, rgba(30,30,30,0.9) 0%, rgba(42,42,42,0.9) 100%)",
          position: "sticky",
          bottom: 0,
          zIndex: 2,
          backdropFilter: "blur(8px)",
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            margin: "0 12px",
            minWidth: "120px",
            padding: "10px 24px",
            fontWeight: "bold",
            color: "#ffffff",
            border: "1px solid #f44336",
            borderRadius: "8px",
            textTransform: "none",
            fontSize: "0.95rem",
            transition: "all 0.3s ease",
            "&:hover": {
              backgroundColor: "rgba(244, 67, 54, 0.1)",
              transform: "translateY(-2px)",
              boxShadow: "0 4px 8px rgba(244, 67, 54, 0.2)",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          sx={{
            margin: "0 12px",
            minWidth: "120px",
            padding: "10px 24px",
            fontWeight: "bold",
            backgroundColor: "#4caf50",
            color: "#000000",
            borderRadius: "8px",
            textTransform: "none",
            fontSize: "0.95rem",
            transition: "all 0.3s ease",
            "&:hover": {
              backgroundColor: "#3d8b40",
              transform: "translateY(-2px)",
              boxShadow: "0 4px 12px rgba(76, 175, 80, 0.3)",
            },
          }}
        >
          Add User
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default AddUserDialog;
